import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Printer, ArrowLeft, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const MAX_ITEM_ROWS = 6;

// Shipping label for one order. Black and white only, so it prints cleanly on thermal printers.
export default function ShippingLabel() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const token = localStorage.getItem('it_token');

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    const load = async () => {
      try {
        const sRes = await axios.get(`${API}/settings`);
        setSeller(sRes.data);
        let found = null;
        try {
          found = (await axios.get(`${API}/admin/orders/${orderId}`, { headers })).data;
        } catch {
          const list = (await axios.get(`${API}/admin/orders?limit=1000`, { headers })).data;
          found = list.find((o) => o.id === orderId || o.order_number === orderId) || null;
        }
        if (!found) { setError('Order not found'); return; }
        setOrder(found);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load order');
      } finally { setLoading(false); }
    };
    load();
  }, [orderId, token]);

  if (loading) return (
    <div className='flex items-center justify-center h-screen bg-slate-50'>
      <div className='w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin' />
    </div>
  );

  if (error) return (
    <div className='flex flex-col items-center justify-center h-screen bg-slate-50 gap-4'>
      <p className='text-red-500 font-semibold'>{error}</p>
      <Link to='/admin/orders' className='text-blue-600 text-sm hover:underline flex items-center gap-1'>
        <ArrowLeft className='w-4 h-4' /> Back to Orders
      </Link>
    </div>
  );

  const show = (key) => seller?.[key] !== false;
  const isA4 = seller?.label_size === 'a4';
  const isCod = (order.payment_method || '').toLowerCase() === 'cod' && order.payment_status !== 'paid';
  const total = Number(order.total || 0);
  const items = order.items || [];
  const shownItems = items.slice(0, MAX_ITEM_ROWS);
  const totalQty = items.reduce((a, i) => a + Number(i.quantity || 1), 0);
  const weightG = (order.shipments || []).reduce((a, s) => a + Number(s.weight_g || 0), 0);
  const tracking = order.tracking || {};
  const pincodes = String(order.delivery_address || '').match(/\b\d{6}\b/g) || [];
  const pincode = pincodes[pincodes.length - 1] || '';
  const sellerName = seller?.seller_name || seller?.store_name || 'IMPORTREND';
  const sellerAddress = [seller?.seller_address, seller?.seller_city, seller?.seller_state, seller?.seller_pincode].filter(Boolean).join(', ');

  const box = 'border-b-2 border-black px-4 py-3';
  const cap = 'text-[10px] font-black uppercase tracking-widest';

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .ship-label, .ship-label * { visibility: visible !important; }
          .ship-label { position: absolute !important; left: 0; top: 0; margin: 0 !important; box-shadow: none !important; }
          .no-print { display: none !important; }
        }
        @page { size: ${isA4 ? 'A4' : '4in 6in'}; margin: ${isA4 ? '10mm' : '0'}; }
      `}</style>

      <div className='no-print bg-slate-800 text-white px-6 py-3 flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <Link to='/admin/orders' className='text-slate-300 hover:text-white flex items-center gap-1.5 text-sm transition-colors'>
            <ArrowLeft className='w-4 h-4' /> Back to Orders
          </Link>
          <span className='text-slate-600'>|</span>
          <span className='text-sm font-semibold'>Shipping label · {order.order_number}</span>
        </div>
        <div className='flex items-center gap-2'>
          <Link to='/admin/settings' className='text-xs text-slate-300 hover:text-white border border-slate-600 rounded-lg px-3 py-1.5 transition-colors'>
            Label settings
          </Link>
          <Link to={`/admin/invoice/${order.id}`} className='flex items-center gap-1.5 text-xs text-slate-300 hover:text-white border border-slate-600 rounded-lg px-3 py-1.5 transition-colors'>
            <FileText className='w-3.5 h-3.5' /> Invoice
          </Link>
          <button
            onClick={() => window.print()}
            data-testid='label-print-btn'
            className='flex items-center gap-1.5 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 py-1.5 font-bold transition-colors'
          >
            <Printer className='w-3.5 h-3.5' /> Print Label
          </button>
        </div>
      </div>

      <div className='bg-slate-100 min-h-screen py-8 px-4'>
        <div
          className='ship-label mx-auto bg-white text-black border-2 border-black shadow-lg overflow-hidden flex flex-col'
          style={isA4 ? { width: '150mm', minHeight: '200mm' } : { width: '4in', height: '6in' }}
          data-testid='shipping-label'
        >
          {/* Brand + payment type */}
          <div className={`${box} flex items-center justify-between`}>
            <div>
              <p className='text-lg font-black tracking-tight leading-none'>{sellerName}</p>
              <p className='text-[10px] mt-1'>Order {order.order_number} · {fmtDate(order.created_at)}</p>
            </div>
            <div className={`px-3 py-1.5 text-sm font-black tracking-widest ${isCod ? 'bg-black text-white' : 'border-2 border-black'}`} data-testid='label-payment-type'>
              {isCod ? 'COD' : 'PREPAID'}
            </div>
          </div>

          {/* Ship to */}
          <div className={box}>
            <p className={cap}>Ship To</p>
            <p className='text-base font-black mt-1 leading-tight' data-testid='label-ship-to-name'>{order.customer_name}</p>
            <p className='text-xs mt-1 leading-snug'>{order.delivery_address}</p>
            <div className='flex items-end justify-between mt-2'>
              {order.customer_phone ? <p className='text-sm font-bold'>Ph: {order.customer_phone}</p> : <span />}
              {pincode && <p className='text-2xl font-black tracking-wider' data-testid='label-pincode'>{pincode}</p>}
            </div>
          </div>

          {/* Shipment details */}
          <div className={`${box} grid grid-cols-3 gap-2 text-[11px]`}>
            <div><p className={cap}>Items</p><p className='font-bold mt-0.5'>{totalQty}</p></div>
            <div><p className={cap}>Weight</p><p className='font-bold mt-0.5'>{weightG > 0 ? `${(weightG / 1000).toFixed(2)} kg` : '-'}</p></div>
            <div><p className={cap}>Order Value</p><p className='font-bold mt-0.5'>Rs. {money(total)}</p></div>
            {(tracking.courier_name || tracking.awb) && (
              <div className='col-span-3'>
                <p className={cap}>Courier / AWB</p>
                <p className='font-bold mt-0.5 font-mono'>{[tracking.courier_name, tracking.awb].filter(Boolean).join(' · ')}</p>
              </div>
            )}
          </div>

          {/* Amount to collect */}
          {show('label_show_cod_amount') && (
            <div className={`${box} text-center`} data-testid='label-collect'>
              {isCod ? (
                <>
                  <p className={cap}>Collect cash on delivery</p>
                  <p className='text-2xl font-black mt-0.5'>Rs. {money(total)}</p>
                </>
              ) : (
                <p className='text-sm font-black tracking-wide'>PREPAID – DO NOT COLLECT CASH</p>
              )}
            </div>
          )}

          {/* Items */}
          {show('label_show_items') && items.length > 0 && (
            <div className={`${box} flex-1`}>
              <p className={cap}>Contents</p>
              <table className='w-full mt-1 text-[10px]'>
                <tbody>
                  {shownItems.map((it, i) => (
                    <tr key={i} className='align-top'>
                      <td className='py-0.5 pr-2'>
                        <span className='font-bold'>{it.product_name || it.name}</span>
                        {it.variant && <span> · {it.variant}</span>}
                        {it.sku && <span className='font-mono'> · {it.sku}</span>}
                      </td>
                      <td className='py-0.5 text-right font-bold whitespace-nowrap'>x {it.quantity || 1}</td>
                    </tr>
                  ))}
                  {items.length > MAX_ITEM_ROWS && (
                    <tr><td colSpan={2} className='py-0.5 italic'>+ {items.length - MAX_ITEM_ROWS} more item(s)</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Return address */}
          {show('label_show_return_address') && (
            <div className='px-4 py-3 mt-auto' data-testid='label-return-address'>
              <p className={cap}>From / Return To</p>
              <p className='text-xs font-black mt-1'>{sellerName}</p>
              {sellerAddress
                ? <p className='text-[10px] leading-snug'>{sellerAddress}</p>
                : <p className='no-print text-[10px] italic text-slate-500'>Add your address in Settings &gt; Invoice &amp; Business Details</p>}
              {show('label_show_seller_phone') && seller?.seller_phone && <p className='text-[10px]'>Ph: {seller.seller_phone}</p>}
              {seller?.seller_gstin && <p className='text-[10px] font-mono'>GSTIN: {seller.seller_gstin}</p>}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
